import React, { useEffect, useState } from "react";
import { MDBBtn, MDBSpinner } from "mdb-react-ui-kit"; // Import MDBSpinner
import jsPDF from "jspdf";
import "jspdf-autotable"; // For table styling
import filterStandingCommittees from "../utils/filterStandingCommittees";
import addCustomFooter from "../utils/addCustomFooter";
import {addDays} from "date-fns";
import ReactGA from "react-ga4";

// Natural Sort function for bills
const naturalSort = (a, b) => {
  const billNumberA = a.billNumber.replace(/[^0-9]/g, ""); // Remove non-numeric characters
  const billNumberB = b.billNumber.replace(/[^0-9]/g, ""); // Remove non-numeric characters
  return parseInt(billNumberA, 10) - parseInt(billNumberB, 10);
};

const generatePDF = async (filteredData, timeframe) => {
  const allMeetings = [];

  filteredData.forEach((committee) => {
    const meetings = committee.meetingTimes;
    meetings.forEach((meeting) => {
      allMeetings.push({
        ...meeting,
        committeeName: committee.committeeDetails.committeeCode.name,
      });
    });
  });

  allMeetings.sort((a, b) => new Date(a.meetingTime) - new Date(b.meetingTime));

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const margin = 36;
  const pageWidth = doc.internal.pageSize.width;
  const logoPath = "/logo192.png";

  const convertToBase64 = async (path) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = path;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
    });
  };

  const logoBase64 = await convertToBase64(logoPath);
  const logoWidth = 40;
  const logoHeight = 40;

  // Add Logo at the top left
  doc.addImage(logoBase64, "PNG", margin, 20, logoWidth, logoHeight);

  // Add Title below logo (move down to give room for everything)
  doc.setFontSize(12);
  doc.text("69th Legislature 2025", pageWidth / 2, margin, {
    align: "center",
  });

  let yPos = margin + 15; // Start below the logo and title

  doc.setFont("helvetica");

  const today = new Date();
  const dateString = today.toISOString().split("T")[0];

  const reportTitleMap = {
    "2day": `2-Day Hearing Calendar`,
    "3day": `3-Day Hearing Calendar`,
    today: `Today's Hearing Calendar`,
    future: `All Future Hearing Calendar`,
  };

  const reportTitle = reportTitleMap[timeframe] || `Committee Meetings`;

  // Add Title at the top of the first page (centered)
  doc.setFontSize(16);
  const titleWidth = doc.getTextWidth(reportTitle);
  const titleX = (doc.internal.pageSize.width - titleWidth) / 2;
  doc.text(reportTitle, titleX, yPos);

  // Update `yPos` after adding the title
  yPos += 30; // Add space after the title

  const contentBottomMargin = 50;
  let lastDate = null; // Track the last date

  // Helper function to check if there's enough space for the section
  const checkAndAddPage = (sectionHeight) => {
    if (
      yPos + sectionHeight >
      doc.internal.pageSize.height - contentBottomMargin
    ) {
      doc.addPage();
      yPos = margin + 30; // Reset yPos after adding title on new page (smaller margin for subsequent pages)
    }
  };

  // Loop through all meetings in chronological order
  allMeetings.forEach((meeting) => {
    const meetingDate = new Date(meeting.meetingTime).toLocaleDateString();
    const meetingTime = new Date(meeting.meetingTime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const location = meeting.location || "No location";
    const committeeName = meeting.committeeName;

    // If the date has changed, add a new section
    if (lastDate !== meetingDate) {
      // Add the date as a section header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(meetingDate, 40, yPos);
      yPos += 10; // Space after the date

      // Draw a divider line
      doc.setDrawColor(0); // Black line
      doc.setLineWidth(0.5);
      doc.line(40, yPos, doc.internal.pageSize.width - 40, yPos);
      yPos += 15; // Space after the divider before the next content
    }

    // Calculate the total section height for the committee (name, date & time, and bills)
    const billsSectionHeight = meeting.bills.reduce((totalHeight, bill) => {
      const billInfo = `${bill.billNumber} - ${bill.shortTitle} (Sponsor: ${
        bill.sponsor || "No sponsor"
      })`;
      const availableWidth = doc.internal.pageSize.width - 150;
      const billInfoWrapped = doc.splitTextToSize(billInfo, availableWidth);
      return totalHeight + billInfoWrapped.length * 12 + 10; // Add space between bills
    }, 0);

    const committeeSectionHeight = 20 + 15 + 15 + billsSectionHeight; // Name, date & time, bills

    // Check if there's enough space for the entire committee section
    checkAndAddPage(committeeSectionHeight);

    // Add committee name only once when the day or time changes
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`${committeeName}`, 40, yPos);
    yPos += 20;

    // Add meeting date, time, and location
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`  ${meetingDate} - ${meetingTime}, ${location}`, 40, yPos);
    yPos += 15;

    // Sort and display bills under the meeting
    const sortedBills = meeting.bills.sort(naturalSort);
    if (sortedBills.length > 0) {
      doc.text("    Bills:", 60, yPos);
      yPos += 10; // Space after 'Bills' title

      sortedBills.forEach((bill) => {
        const billInfo = `${bill.billNumber} - ${bill.shortTitle} (Sponsor: ${
          bill.sponsor || "No sponsor"
        })`;
        const availableWidth = doc.internal.pageSize.width - 150;
        const billInfoWrapped = doc.splitTextToSize(billInfo, availableWidth);

        // Check if there's enough space for the bill info
        if (
          yPos + billInfoWrapped.length * 12 >
          doc.internal.pageSize.height - contentBottomMargin
        ) {
          doc.addPage();
          yPos = margin + 30; // Reset yPos after adding title on new page (smaller margin for subsequent pages)
        }

        billInfoWrapped.forEach((line, index) => {
          doc.text(`      ${line}`, 80, yPos);
          yPos += 12;
        });
      });
    } else {
      doc.text("    No bills listed.", 60, yPos);
      yPos += 15;
    }

    yPos += 20; // Add some space after each meeting group

    // Update lastDate to the current meeting's date
    lastDate = meetingDate;
  });

  // Add footer after all pages are generated
  addCustomFooter(doc, margin);

  const fileNameMap = {
    "2day": `2-Day-Hearing-Calendar-${dateString}`,
    "3day": `3-Day-Hearing-Calendar-${dateString}`,
    today: `Todays-Hearings-Calendar-${dateString}`,
    future: `All-Future-Hearings-Calendar-${dateString}`,
  };

  doc.save(
    `${fileNameMap[timeframe] || `Committee-Calendar-${dateString}`}.pdf`
  );
};

const ExportQuickLinks = ({ filteredCommittees , loadingExtraData }) => {
  const [loading, setLoading] = useState(true);

  // useEffect hook to trigger loading state change when data is available
  useEffect(() => {
    if (filteredCommittees && filteredCommittees.length > 0) {
      setLoading(false); // Set loading to false once data is available
    }
  }, [filteredCommittees]);


  const handleExport = async (timeframe) => {
    setLoading(true);
    if (!filteredCommittees) {
      console.error("Data is not available.");
      return;
    }

    ReactGA.event({
      category: "User Interaction",
      action: "PDF download",
      label: "Download",
    });

    const today = new Date();
    let dateRange = null;

    if (timeframe === "today") {
      dateRange = { start: today, end: today };
    } else if (timeframe === "2day") {
      dateRange = { start: today, end: addDays(today, 2) };
    } else if (timeframe === "3day") {
      dateRange = { start: today, end: addDays(today, 3) };
    }
    const updatedCommittees = await filterStandingCommittees(
        filteredCommittees,
        dateRange,
        { legs: [], roles: [] },
        [],
        false
    );
    await generatePDF(updatedCommittees, timeframe);
    setLoading(false);
  };

  return (
    <div id="quick-links" className="d-flex align-items-center">
      <strong>Quick Reports:</strong>
      {loadingExtraData ? (
        <>
          {/* Show Loading message with a spinner */}
          <span className="mx-3">
            <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
          </span>
        </>
      ) : (
        <>
          {/* Show the export buttons when data is available */}
          <MDBBtn
            color="link"
            rippleColor="light"
            onClick={() => handleExport("today")}
          >
            Today's Hearings
          </MDBBtn>
          <MDBBtn
            color="link"
            rippleColor="light"
            onClick={() => handleExport("2day")}
          >
            2 Day Hearings
          </MDBBtn>
          <MDBBtn
            color="link"
            rippleColor="light"
            onClick={() => handleExport("3day")}
          >
            3 Day Hearings
          </MDBBtn>
          <MDBBtn
            color="link"
            rippleColor="light"
            onClick={() => handleExport("future")}
          >
            All Future Hearings
          </MDBBtn>
        </>
      )}
    </div>
  );
};

export default ExportQuickLinks;
